import { createReducer, on } from '@ngrx/store';
import { DateTime } from 'luxon';

import { MessagesActions } from './messages.actions';
import { MessagesParticipantMessagesStoreModel, MessagesSystemMessagesStoreModel } from './models';

export const featureKey = 'messages';

export interface MessagesState {
  isLoading: boolean;
  isMessagesListPolling: boolean;
  unreadMessagesCount?: number;
  lastGetParticipantUnreadMessagesRequestTimestamp?: number;
  systemMessages?: MessagesSystemMessagesStoreModel;
  participantMessages?: MessagesParticipantMessagesStoreModel;
}

const initialState: MessagesState = {
  isLoading: false,
  isMessagesListPolling: false,
  unreadMessagesCount: undefined,
  lastGetParticipantUnreadMessagesRequestTimestamp: undefined,
  systemMessages: undefined,
  participantMessages: undefined,
};

export const messagesReducer = createReducer(
  initialState,

  on(
    MessagesActions.getParticipantUnreadMessages,
    (state): MessagesState => ({
      ...state,
      lastGetParticipantUnreadMessagesRequestTimestamp: DateTime.now().toMillis(),
    })
  ),
  on(
    MessagesActions.getParticipantUnreadMessagesSuccess,
    (state, { count }): MessagesState => ({
      ...state,
      unreadMessagesCount: count,
    })
  ),

  on(
    MessagesActions.getSystemMessages,
    (state, { isPolling }): MessagesState => ({
      ...state,
      isLoading: !isPolling,
      isMessagesListPolling: !!isPolling,
    })
  ),
  on(
    MessagesActions.getSystemMessagesSuccess,
    (state, { messages, action, totalCount }): MessagesState => ({
      ...state,
      isLoading: false,
      isMessagesListPolling: false,
      systemMessages: { messages, action, totalCount },
    })
  ),
  on(
    MessagesActions.getSystemMessagesFail,
    (state): MessagesState => ({
      ...state,
      isLoading: false,
      isMessagesListPolling: false,
      systemMessages: undefined,
    })
  ),
  on(
    MessagesActions.incorrectGetSystemMessagesRequest,
    (state): MessagesState => ({
      ...state,
      systemMessages: { ...state.systemMessages, messages: [] },
    })
  ),

  on(
    MessagesActions.getParticipantMessages,
    (state, { isPolling }): MessagesState => ({
      ...state,
      isLoading: !isPolling,
      isMessagesListPolling: !!isPolling,
    })
  ),
  on(
    MessagesActions.getParticipantMessagesSuccess,
    (state, { messages, action, totalCount }): MessagesState => ({
      ...state,
      isLoading: false,
      isMessagesListPolling: false,
      participantMessages: { messages, action, totalCount },
    })
  ),
  on(
    MessagesActions.getParticipantMessagesFail,
    (state): MessagesState => ({
      ...state,
      isLoading: false,
      isMessagesListPolling: false,
      participantMessages: undefined,
    })
  ),
  on(
    MessagesActions.incorrectGetParticipantMessagesRequest,
    (state): MessagesState => ({
      ...state,
      participantMessages: { ...state.participantMessages, messages: [] },
    })
  ),

  on(MessagesActions.postManualMessage, (state): MessagesState => ({ ...state, isLoading: true })),
  on(MessagesActions.postManualMessageSuccess, (state): MessagesState => ({ ...state, isLoading: false })),
  on(MessagesActions.postManualMessageFail, (state): MessagesState => ({ ...state, isLoading: false })),

  on(
    MessagesActions.clearSystemMessages,
    (state): MessagesState => ({
      ...state,
      systemMessages: undefined,
      isLoading: false,
      isMessagesListPolling: false,
    })
  ),
  on(
    MessagesActions.clearParticipantMessages,
    (state): MessagesState => ({
      ...state,
      participantMessages: undefined,
      isLoading: false,
      isMessagesListPolling: false,
    })
  ),
  on(
    MessagesActions.clearUnreadMessages,
    (state): MessagesState => ({
      ...state,
      unreadMessagesCount: undefined,
      lastGetParticipantUnreadMessagesRequestTimestamp: undefined,
    })
  )
);
